import { File } from "@/types/app/file";
import { ParameterItem, ParametersDetailsSmall } from "@components/llama/Configs/PromptDetails/ParametersDetails";
import React, { useEffect, useRef, useState } from "react";
import { EntityIcon } from "@components/library/Entities/EntityIcon";
import { Tag } from "../Tag";
import { getShortId } from "@/lib/string";
import { hlToast, hlToastApiError, ToastVariant } from "../Toast";
import Avatar from "@components/atoms/Avatar";
import { DefaultEnvironmentTag } from "@components/PromptEditor/VariableTags";
import Tooltip from "../Tooltip";
import { updateVersionDetails } from "@/services/files.service";
import Button from "@components/library/Button";
import { useVersionDrawer } from "@components/llama/VersionDrawer/useVersionDrawer";
import { ArrowsOutSimple } from "@phosphor-icons/react";
import axios from "axios";
import { Error } from "@components/molecules/FormikInput";
import { classNames } from "../utils/classNames";
import { normalizeVersionFields } from "../utils/versions";

interface Props {
  file: File;
  showOpenButton?: boolean;
  onFileChange: (file: File) => void; // Update cache and trigger revalidation
  isEditable?: boolean; // Used by VersionDetailPanel (which is used in the VersionPickerStep) so that this popover can be used for display only

  onEditingStart?: () => void;
  onEditingEnd?: () => void;
  onErrorOccurred?: () => void;
  onErrorResolved?: () => void;
  onOpenDrawer?: () => void;
}

/**
 * This component is only the card. For the Label and Tooltip, use `FileLabelWithPopover`.
 */
export const VersionPopover = ({
  file,
  onFileChange,
  showOpenButton = true,
  isEditable = true,
  onEditingStart,
  onEditingEnd,
  onErrorOccurred,
  onErrorResolved,
  onOpenDrawer,
}: Props) => {
  return (
    <div
      className={classNames(
        "flex w-[344px] max-w-sm flex-col rounded-lg bg-background-base-1 shadow-card-sm",
        !isEditable && "pointer-events-none",
      )}
    >
      <VersionFileName file={file} />

      <VersionHeader
        file={file}
        onFileChange={onFileChange}
        isEditable={isEditable}
        onEditingStart={onEditingStart}
        onEditingEnd={onEditingEnd}
        onErrorOccurred={onErrorOccurred}
        onErrorResolved={onErrorResolved}
        showOpenButton={showOpenButton}
        onOpenDrawer={onOpenDrawer}
      />

      <VersionTags file={file} />

      <div className="mt-10 flex w-full flex-col justify-between border-b  border-t border-stroke-base-25 bg-background-base-2">
        <VersionDescription
          file={file}
          onFileChange={onFileChange}
          isEditable={isEditable}
          onEditingStart={onEditingStart}
          onEditingEnd={onEditingEnd}
        />
        <VersionParameters file={file} />
      </div>

      <div className="flex h-44 items-center justify-between p-12">
        <VersionAuthor file={file} />
        <VersionId file={file} />
      </div>
    </div>
  );
};

const VersionFileName = ({ file }: { file: File }) => {
  return (
    <div className="mt-12 flex h-14 items-center justify-between gap-6 px-12">
      <div className="flex items-center gap-6 overflow-hidden">
        <EntityIcon type={file.type} size={14} />
        <span title={file.name} className="truncate text-12-12 font-medium text-text-base-2">
          {file.name}
        </span>
      </div>
    </div>
  );
};

const inputStyles = {
  // Container styles for editable inputs
  container: (isEditable: boolean) =>
    classNames(
      "flex w-full overflow-hidden rounded-ms",
      "transition-[border,box-shadow] duration-100",
      isEditable
        ? [
            "border border-transparent",
            "hover:border-stroke-base-4",
            "focus-within:border",
            "focus-within:border-stroke-secondary-4",
            "focus-within:ring-[2px]",
            "focus-within:hover:border-stroke-secondary-4",
          ].join(" ")
        : "pointer-events-none",
    ),

  // Base input styles shared by both input and textarea
  base: (isEditable: boolean) =>
    classNames("w-full border-none px-4 py-4", isEditable ? "focus:ring-0 focus:outline-none" : "pointer-events-none"),
};

const VersionHeader = ({
  file,
  onFileChange,
  showOpenButton,
  isEditable,
  onEditingStart,
  onEditingEnd,
  onErrorOccurred,
  onErrorResolved,
  onOpenDrawer,
}: {
  file: File;
  onFileChange: (file: File) => void;
  showOpenButton: boolean;
  isEditable: boolean;
  onEditingStart?: () => void;
  onEditingEnd?: () => void;
  onErrorOccurred?: () => void;
  onErrorResolved?: () => void;
  onOpenDrawer?: () => void;
}) => {
  const [nameError, setNameError] = useState<string | undefined>(undefined);
  const [versionName, setVersionName] = useState(file.version_name || "");
  const [isEditing, setIsEditing] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  // Ref to track if save is in progress to prevent double-save (when pressing enter key which triggers blur as well)
  const isSavingRef = useRef(false);

  useEffect(() => {
    setVersionName(file.version_name || "");
  }, [file.version_name]);

  // Notify parent component of state changes
  useEffect(() => {
    if (nameError) {
      onErrorOccurred?.();
    } else {
      onErrorResolved?.();
    }
  }, [nameError, onErrorOccurred, onErrorResolved]);

  const startEditing = () => {
    if (isEditable && !isEditing) {
      setIsEditing(true);
      onEditingStart?.();
    }
  };

  const cancelEditing = () => {
    setIsEditing(false);
    setVersionName(file.version_name || "");
    setNameError(undefined);
    onEditingEnd?.();
    onErrorResolved?.();
  };

  const endEditingSuccess = () => {
    setIsEditing(false);
    onEditingEnd?.();
  };

  const handleError = (errorMessage: string) => {
    setNameError(errorMessage);
    setIsEditing(true);
    onErrorOccurred?.();

    // Focus back on input to make it clear to the user they need to change the name
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  const handleNameChange = async (): Promise<boolean> => {
    if (isSavingRef.current) {
      return false;
    }

    const nameNormalized = normalizeVersionFields.name(versionName);
    if (file.version_name === nameNormalized) {
      endEditingSuccess();
      return true;
    }

    try {
      isSavingRef.current = true;
      setNameError(undefined);
      await updateVersionDetails(file.id, file.version_id, { name: nameNormalized });

      onFileChange({ ...file, version_name: nameNormalized });

      hlToast({
        title: "Version name updated",
        variant: ToastVariant.Success,
      });
      endEditingSuccess();
      return true;
    } catch (error: any) {
      if (axios.isAxiosError(error) && error.response?.status === 409) {
        handleError("This version name already exists. Please choose a different name.");
      } else {
        console.error("Error renaming version:", error);
        hlToastApiError({ error: error, titleFallback: "Failed to rename version" });
        cancelEditing();
      }
      return false;
    } finally {
      // Small delay to prevent double-save from Enter+blur events
      // See similar pattern in VersionName component
      setTimeout(() => {
        isSavingRef.current = false;
      }, 100);
    }
  };

  const handleSave = async () => {
    const success = await handleNameChange();
    if (success) {
      inputRef.current?.blur();
    }
  };

  const handleBlur = () => {
    // If there's already an error and the user blurs without fixing it,
    // reset the name to the original value
    if (nameError) {
      cancelEditing();
      return;
    }

    // Otherwise proceed with normal save
    handleNameChange();
  };

  return (
    <div className="mx-8 mt-10">
      {/* Top row with name input + actions */}
      <div className="flex items-center justify-between gap-12">
        {/* Name input */}
        <div className={inputStyles.container(isEditable)}>
          {isEditable && isEditing ? (
            <input
              ref={inputRef}
              type="text"
              value={versionName}
              placeholder="Version name"
              className={classNames(
                inputStyles.base(isEditable),
                "bg-background-base-1",
                "text-12-16 font-medium text-text-base-1",
                "placeholder:text-text-base-1 focus:placeholder:text-text-base-4",
                "h-24",
              )}
              autoFocus
              tabIndex={-1}
              readOnly={!isEditable}
              onFocus={
                isEditable
                  ? (e) => {
                      e.currentTarget.placeholder = "Version name";
                      e.currentTarget.select();
                    }
                  : undefined
              }
              onChange={(e) => {
                setVersionName(e.target.value);
                setNameError(undefined);
              }}
              onBlur={handleBlur}
              onKeyDown={async (e: React.KeyboardEvent<HTMLInputElement>) => {
                if (e.key === "Enter") {
                  await handleSave();
                }
                if (e.key === "Escape") {
                  cancelEditing();
                }
              }}
            />
          ) : (
            <div
              className={classNames(
                inputStyles.base(isEditable),
                "flex w-full cursor-text items-center overflow-hidden",
                "bg-background-base-1",
                "h-24",
              )}
              onClick={startEditing}
            >
              {versionName ? (
                <span className="truncate text-12-16 font-medium text-text-base-1" title={versionName}>
                  {versionName}
                </span>
              ) : (
                <div className="flex items-center gap-2">
                  <EntityIcon type="hash" size={12} />
                  <span className="font-mono text-12-14 font-medium text-text-base-1">
                    {getShortId(file.version_id)}
                  </span>
                </div>
              )}
            </div>
          )}
        </div>
        {/* Actions placed on the right of the name input. Always vertically aligned with the name input */}
        <div className="flex-none">
          <VersionActions
            file={file}
            showOpenButton={showOpenButton}
            isEditable={isEditable}
            onOpenDrawer={onOpenDrawer}
          />
        </div>
      </div>
      {/* Error message below row with input + actions */}
      <Error name="version-name" error={nameError} />
    </div>
  );
};

const VersionActions = ({
  file,
  showOpenButton,
  isEditable,
  onOpenDrawer,
}: {
  file: File;
  showOpenButton: boolean;
  isEditable: boolean;
  onOpenDrawer?: () => void;
}) => {
  const { setOpen: setDrawerOpen, setSelectedVersion } = useVersionDrawer();

  if (!showOpenButton || !isEditable) {
    return null;
  }

  return (
    <Button
      size={20}
      styling="outline"
      elevated
      IconLeft={ArrowsOutSimple}
      onClick={() => {
        setSelectedVersion(file);
        setDrawerOpen(true);
        onOpenDrawer?.();
      }}
    >
      Open
    </Button>
  );
};

const VersionTags = ({ file }: { file: File }) => {
  // Skip rendering if there are no environments
  if (!file.environments || file.environments.length === 0) {
    return null;
  }

  return (
    <div className="mt-10 flex h-20 flex-wrap gap-8 px-12 text-12-14 font-normal text-text-base-2">
      {file.environments.map((environment) => {
        if (environment.tag === "default") {
          return <DefaultEnvironmentTag key={environment.id} name={environment.name} />;
        }
        return (
          <Tag size={20} key={environment.id}>
            {environment.name}
          </Tag>
        );
      })}
    </div>
  );
};

const VersionParameters = ({ file }: { file: File }) => {
  if (!["prompt", "dataset", "evaluator"].includes(file.type)) {
    return null;
  }
  return (
    <div className="px-[12px] pb-[12px]">
      {file.type === "prompt" && <ParametersDetailsSmall {...file} />}
      {file.type === "dataset" && <ParameterItem label="datapoints" value={file.datapoints_count} />}
      {file.type === "evaluator" && (
        <div className="flex flex-wrap items-center gap-x-[12px] gap-y-[4px]">
          <ParameterItem label="type" value={file.spec.evaluator_type} />
          <ParameterItem label="response" value={file.spec.return_type} />
          <ParameterItem label={file.spec.arguments_type} value="" />
        </div>
      )}
    </div>
  );
};

const VersionDescription = ({
  file,
  onFileChange,
  isEditable,
  onEditingStart,
  onEditingEnd,
}: {
  file: File;
  onFileChange: (file: File) => void;
  isEditable: boolean;
  onEditingStart?: () => void;
  onEditingEnd?: () => void;
}) => {
  const [versionDescription, setVersionDescription] = useState(file.version_description || "");
  const textAreaRef = useRef<HTMLTextAreaElement>(null);
  const isSavingRef = useRef(false);

  useEffect(() => {
    // For some reason, if FileLabelWithPopover takes in versionPopoverProps,
    // the textarea auto-focuses on mount :'(. This is a hack to prevent this.
    const timer = setTimeout(() => {
      // After a small delay, reset any auto-focus that might have happened
      if (textAreaRef.current && document.activeElement === textAreaRef.current) {
        textAreaRef.current.blur();
      }
    }, 10);

    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    setVersionDescription(file.version_description || "");
  }, [file.version_description]);

  const startEditing = () => {
    if (isEditable) {
      onEditingStart?.();
    }
  };

  const cancelEditing = () => {
    setVersionDescription(file.version_description || "");
    onEditingEnd?.();
  };

  const endEditingSuccess = () => {
    onEditingEnd?.();
  };

  const handleDescriptionChange = async (): Promise<boolean> => {
    if (isSavingRef.current) {
      return false;
    }

    const descriptionNormalized = normalizeVersionFields.description(versionDescription);
    if (file.version_description === descriptionNormalized) {
      endEditingSuccess();
      return true;
    }

    try {
      isSavingRef.current = true;
      await updateVersionDetails(file.id, file.version_id, { description: descriptionNormalized });

      onFileChange({ ...file, version_description: descriptionNormalized });

      hlToast({
        title: "Description updated",
        variant: ToastVariant.Success,
      });
      return true;
    } catch (error: any) {
      console.error("Error updating description:", error);
      hlToastApiError({ error: error, titleFallback: "Failed to update description" });
      return false;
    } finally {
      // Reset the saving flag after a short delay
      // This prevents the onBlur handler from triggering another save
      // when Enter key causes both keyDown and subsequent blur events
      setTimeout(() => {
        isSavingRef.current = false;
      }, 100);
    }
  };

  const handleSave = async () => {
    const success = await handleDescriptionChange();
    if (success) {
      endEditingSuccess();
      textAreaRef.current?.blur();
    }
  };

  return (
    <div className="p-8">
      <div className={inputStyles.container(isEditable)}>
        <textarea
          ref={textAreaRef}
          placeholder="No description"
          value={versionDescription}
          className={classNames(
            inputStyles.base(isEditable),
            "h-64 resize-none bg-background-base-2",
            "text-13-20 font-normal text-text-base-1",
            "placeholder:text-text-base-3",
            "scrollbar-thin scrollbar-track-transparent scrollbar-thumb-stroke-base-3",
          )}
          onChange={(e) => {
            setVersionDescription(e.target.value);
          }}
          onClick={startEditing}
          onBlur={handleSave}
          onKeyDown={async (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              await handleSave();
            }
            if (e.key === "Escape") {
              cancelEditing();
            }
          }}
        />
      </div>
    </div>
  );
};

const VersionAuthor = ({ file }: { file: File }) => {
  const user = file.created_by;
  const timestamp = file.created_at;

  return (
    <div className="flex  items-center ">
      {user && (
        <div className="flex items-center  gap-4 truncate">
          <Avatar user={user} size={20} />
          <span
            className="max-w-100 truncate pl-4 text-12-14 font-medium text-text-base-1"
            title={`${user.full_name} (${user.email_address})`}
          >
            {user.full_name}
          </span>
          <span
            className="truncate text-12-14 font-normal text-text-base-2"
            title={timestamp.format("YYYY-MM-DD HH:mm:ss")}
          >
            created {timestamp.fromNow()}
          </span>
        </div>
      )}
    </div>
  );
};

const VersionId = ({ file }: { file: File }) => {
  return (
    <div
      className="flex items-center gap-6 pl-4"
      onClick={() => {
        navigator.clipboard.writeText(file.version_id);
        hlToast({
          title: "Version ID copied to clipboard",
          variant: ToastVariant.Success,
        });
      }}
    >
      <Tooltip
        content={
          <span className="whitespace-nowrap">
            Version ID: <span className="!font-mono">{file.version_id}</span>{" "}
          </span>
        }
      >
        <div className="flex items-center">
          <span className="cursor-pointer font-mono text-12-12 text-text-base-2">{getShortId(file.version_id)}</span>
          <span className="text-12-12 font-normal text-text-base-2">...</span>
        </div>
      </Tooltip>
    </div>
  );
};
